import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import styles from "../styles.module.scss";
import localStyles from "./styles.module.scss";
import PagePanel from "app/components/PagePanel";
import Tabs from "app/components/Tabs";
import { useAdminSettingsTabs } from "app/routes/Facility/adminSettingsTabs";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";
import {
  getFacilityHolidays,
  createFacilityHoliday,
  updateFacilityHoliday,
  deleteFacilityHoliday,
  HolidayResponse,
  HolidayPayload,
} from "app/utils/request/scheduling/facilityHolidays";
import { Loading } from "app/components/Wrappers";
import CancelPurchaseConfirmationModal from "app/components/CancelPurchaseConfirmationModal";
import HolidaysView from "./HolidaysView";

const Holidays = () => {
  const { t } = useTranslation();
  const currentFacility = useCurrentFacility();
  const tabs = useAdminSettingsTabs("holidays");

  // State for holidays and loading
  const [holidays, setHolidays] = useState<HolidayResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(false);

  // State for delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Facility data
  const facilityId = currentFacility.data?.id;

  // Fetch holidays when facility is available
  useEffect(() => {
    if (!facilityId) {
      return;
    }
    setIsLoading(true);
    getFacilityHolidays(facilityId)
      .then((holidaysData) => setHolidays(holidaysData))
      .catch((error) => {
        console.error("Error fetching holidays:", error);
        toast.error(t("Failed to load holidays."));
      })
      .finally(() => setIsLoading(false));
  }, [facilityId, refreshTrigger, t]);

  // Handler functions for CRUD operations
  const handleAddHoliday = async (payload: HolidayPayload) => {
    if (!facilityId) {
      return;
    }
    try {
      await createFacilityHoliday(facilityId, payload);
      setRefreshTrigger(!refreshTrigger);
      toast.success(t("Holiday created successfully."));
    } catch (error) {
      console.error("Error creating holiday:", error);
      if ((error as any)?.errors?.date) {
        toast.error(t("This date is already set as a holiday."));
      } else {
        toast.error(t("Failed to create holiday."));
      }
    }
  };

  const handleEditHoliday = async (
    holidayId: number,
    payload: HolidayPayload
  ) => {
    if (!facilityId) {
      return;
    }
    try {
      await updateFacilityHoliday(facilityId, holidayId, payload);
      setRefreshTrigger(!refreshTrigger);
      toast.success(t("Holiday updated successfully."));
    } catch (error) {
      console.error("Error updating holiday:", error);
      if ((error as any)?.errors?.date) {
        toast.error(t("This date is already set as a holiday."));
      } else {
        toast.error(t("Failed to update holiday."));
      }
    }
  };

  const handleDeleteHoliday = async (holidayId: number) => {
    setPendingDeleteId(holidayId);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!facilityId || pendingDeleteId === null) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteFacilityHoliday(facilityId, pendingDeleteId);
      setRefreshTrigger(!refreshTrigger);
      toast.success(t("Holiday deleted successfully."));
    } catch (err) {
      const error = err as any;
      console.error("Error deleting holiday:", error);
      toast.error(t("Failed to delete holiday."));
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setPendingDeleteId(null);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setPendingDeleteId(null);
  };

  return (
    <div className="admin-settings-container">
      <div className={styles.title}>
        <h1>{t("Admin Settings")}</h1>
      </div>
      <div>
        <div className={styles.tabs_container}>
          <Tabs tabs={tabs} scrollable />
        </div>
        <PagePanel title={t("Holidays")} titleStyles={styles.header}>
          <p className={localStyles.subtitle}>
            {t(
              "Define holidays for your facility."
            )}
          </p>

          {isLoading ? (
            <Loading isLoading loadType="spinner" />
          ) : (
            <HolidaysView
              holidays={holidays}
              onAdd={handleAddHoliday}
              onEdit={handleEditHoliday}
              onDelete={handleDeleteHoliday}
            />
          )}
        </PagePanel>
      </div>
      <CancelPurchaseConfirmationModal
        isOpen={isDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isDeleting}
        title={t("Delete Holiday")}
        message={t("Are you sure you want to delete this holiday?")}
      />
    </div>
  );
};

export default Holidays;
